<template>
  <div class="lab-area-renderer h-100">
    <div
      v-if="hasLabArea"
      class="text-start d-flex flex-column px-lg-4 py-lg-3 h-100 position-relative"
      :class="{ 'bg-dots': !showSimpleUI }"
    >
      <div
        class="row mb-3 flex-grow-1"
        :class="{ 'opacity-50': labAreaLocked }"
        :inert="labAreaLocked"
      >
        <component
          :is="labAreaComponent"
          :lab-id="effectiveLabId"
          :context="context"
          :current-attempt="currentAttempt"
          :showDebugUI="showDebugUI"
          :showSimpleUI="showSimpleUI"
          @lab-data-updated="$emit('lab-data-updated', $event)"
          @scrollToOverview="$emit('scrollToOverview')"
        />
      </div>

      <div
        v-if="labAreaLocked"
        class="lab-area-lock-overlay position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center p-3"
        role="region"
        aria-labelledby="lab-area-lock-heading"
      >
        <div
          class="card shadow border-2 lab-area-lock-card m-0"
          style="max-width: 28rem; z-index: 2"
        >
          <div class="card-body">
            <h3 id="lab-area-lock-heading" class="h6 card-title fw-semibold">
              Lab area locked
            </h3>
            <p class="card-text small mb-0">
              The instruments stay unavailable until you reach
              <strong>Step 1</strong> of the experiment.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-muted text-center py-4">
      Lab area not available for this activity.
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { getLabAreaLoader } from "@/labRegistry.js";

const asyncComponentByLabId = new Map();

/**
 * Returns the async component for a lab ID
 * @param {string} labId
 */
function getLabAreaAsyncComponent(labId) {
  const loader = getLabAreaLoader(labId);
  if (!loader) return null;
  if (!asyncComponentByLabId.has(labId)) {
    asyncComponentByLabId.set(labId, defineAsyncComponent(loader));
  }
  return asyncComponentByLabId.get(labId);
}

export default {
  name: "LabAreaRenderer",
  props: {
    /** When null, uses `import.meta.env.VITE_LAB_ENV`. */
    labId: {
      type: String,
      default: null
    },
    context: {
      type: Object,
      default: () => ({})
    },
    currentAttempt: {
      type: Object,
      default: null
    },
    showDebugUI: {
      type: Boolean,
      required: true
    },
    showSimpleUI: {
      type: Boolean,
      required: true
    },
    labAreaLocked: {
      type: Boolean,
      default: false
    }
  },
  emits: ["lab-data-updated", "scrollToOverview"],
  computed: {
    effectiveLabId() {
      return this.labId ?? import.meta.env.VITE_LAB_ENV;
    },
    labAreaComponent() {
      return getLabAreaAsyncComponent(this.effectiveLabId);
    },
    hasLabArea() {
      return this.labAreaComponent != null;
    }
  }
};
</script>

<style scoped>
.lab-area-lock-overlay {
  z-index: 10;
  background: rgba(var(--bs-body-bg-rgb, 255, 255, 255), 0.72);
  pointer-events: auto;
}

[data-bs-theme="dark"] .lab-area-lock-overlay {
  background: rgba(var(--bs-body-bg-rgb, 33, 37, 41), 0.82);
}
</style>
